import { ProxyWorkflowStatusType } from '../../../core/models/proxy-workflow-status-type';
import { WorkflowStatusType } from '../../../core/models/workflow-status-type';

/**
 * Single source of truth for mapping workflow status ids to badge chips. Used
 * by the dashboard tables, the new-linking-requests queue, the proxy queue,
 * and the linking-request detail screen — drift between copies of this mapping
 * means staff read different labels for the same request depending on which
 * screen they're on, so don't fork it back into a feature.
 */

/** Modifier key for the shared `.badge--*` classes (see styles/partials/_badges.scss). */
export type RequestStatusKind =
  | 'pending'
  | 'new'
  | 'needs-info'
  | 'in-progress'
  | 'in-review'
  | 'approved'
  | 'denied'
  | 'unknown';

export interface StatusBadge {
  label: string;
  kind: RequestStatusKind;
}

/**
 * Fallback badge for status ids the UI doesn't name, and for a missing id.
 * Deliberately "Unknown", not a real status: on a PHI triage queue,
 * mislabeling an unrecognized status as a real one makes staff triage the
 * wrong rows — admitting ignorance is safer. `kind: 'unknown'` renders in the
 * same neutral treatment as `pending` (see styles/partials/_badges.scss) but
 * stays a distinct hook, so a screen can style "we don't recognize this"
 * differently from a real neutral status.
 */
const UNKNOWN_BADGE: StatusBadge = { label: 'Unknown', kind: 'unknown' };

// The API can grow statuses the UI doesn't name, so any lookup can miss at
// runtime — the getters fall back to UNKNOWN_BADGE.
const STATUS_BADGES: Record<number, StatusBadge | undefined> = {
  [WorkflowStatusType.Pending]: { label: 'Pending', kind: 'pending' },
  [WorkflowStatusType.New]: { label: 'New', kind: 'new' },
  [WorkflowStatusType.NeedsInfo]: { label: 'Needs Info', kind: 'needs-info' },
  [WorkflowStatusType.InProgress]: { label: 'In Progress', kind: 'in-progress' },
};

/**
 * The proxy queue's own fallback. It can't reuse `kind: 'pending'`, which that
 * queue deliberately re-tints to the primary (in-flight) treatment — a
 * `pending` Unknown chip there would read as an active workflow step. `unknown`
 * is the neutral grey the global `.badge--unknown` rule provides, and the proxy
 * queue doesn't re-tint it (see styles/partials/_badges.scss).
 */
const PROXY_UNKNOWN_BADGE: StatusBadge = { label: 'Unknown', kind: 'unknown' };

/**
 * The caregiver/proxy queue has its own status set (ProxyWorkflowStatusType)
 * and its own vocabulary: the labels name what each status is *waiting on*
 * rather than the internal status, because staff triage this queue by whose
 * action is outstanding. The `kind` keys stay the status-derived CSS hooks, so
 * the shared `.badge--*` colors keep applying after the rewording.
 */
const PROXY_STATUS_BADGES: Record<number, StatusBadge | undefined> = {
  // Pending is the request's opening state: submitted, not yet acted on by the
  // patient or by staff.
  [ProxyWorkflowStatusType.Pending]: { label: 'Pending', kind: 'pending' },
  // In Review means the request is sitting with staff, so the chip names what
  // the patient is waiting on rather than the internal status.
  [ProxyWorkflowStatusType.InReview]: { label: 'Awaiting Review', kind: 'in-review' },
  // NeedsInfo carries the "auth email sent, patient has not confirmed yet" step
  // of the proxy workflow — the queue names it for the confirmation it awaits.
  [ProxyWorkflowStatusType.NeedsInfo]: { label: 'Awaiting Confirmation', kind: 'needs-info' },
  // An approved proxy is a live delegation, so staff read it as Active.
  [ProxyWorkflowStatusType.Approved]: { label: 'Active', kind: 'approved' },
  [ProxyWorkflowStatusType.Denied]: { label: 'Denied', kind: 'denied' },
};

/**
 * Resolve the badge for a linking request, on the queues and on the request
 * detail screen. The chip is driven purely by the workflow status id —
 * `isOverdue` does not affect it (same rule as the dashboard chips); an
 * unrecognized status renders as Unknown.
 *
 * The id is optional because the detail screen's `RequestDetail.workflowStatusTypeId`
 * is not returned by every API path yet; a missing id reads as Unknown rather
 * than asserting a status the request isn't in.
 */
export function getStatusBadge(workflowStatusTypeId: number | null | undefined): StatusBadge {
  if (workflowStatusTypeId == null) {
    return UNKNOWN_BADGE;
  }
  return STATUS_BADGES[workflowStatusTypeId] ?? UNKNOWN_BADGE;
}

/**
 * Resolve the badge for a caregiver/proxy request. Driven purely by the workflow
 * status id — `isOverdue` does not affect it (same rule as the dashboard chips).
 * An unrecognized status renders as a neutral Unknown chip so a new backend
 * status can neither render unstyled nor be mistaken for a named workflow step:
 * the proxy row's actions key off the raw status id, so a chip naming a real
 * step would offer no matching action.
 */
export function getProxyStatusBadge(workflowStatusTypeId: number): StatusBadge {
  return PROXY_STATUS_BADGES[workflowStatusTypeId] ?? PROXY_UNKNOWN_BADGE;
}
